/* LT-Prelims-01 — Tender Prelims tab.
 *
 * Sections:
 *   1) Master inputs strip (4 cards): hours, man-days, project duration (with
 *      manual adjustment input + sage tooltip about Adjudication), net-total +
 *      prelims % traffic light.
 *   2) Hide-unused toggle + Collapse-all toggle + auto-save status pill.
 *   3) Seven section cards (Staff / Welfare / Plant / Scaffolding / Fencing /
 *      Cleaning / Misc) each with its own table of prelim line rows. Per-row
 *      qty + weeks + rate + total cells (cells disabled per unit_type), inline
 *      Note popover, three-dot menu (View audit / Reset / Delete-custom). Add
 *      custom line at the bottom of each card.
 *   4) Client-facing breakdown panel — 4 categories, click to expand and see
 *      contributing line items.
 *   5) Audit drawer — right-side slide-in (560px) showing per-field changes.
 *
 * State model:
 *   `data` mirrors the backend shape `{ items, summary, tender, seeded }`.
 *   Local optimistic updates write into `data.items`; an 800ms-debounced
 *   `flushSave()` flushes pending PUTs to /api/tenders/{id}/prelims/{itemId}.
 *   Server response replaces `data` so summary + traffic light + cascaded
 *   weeks all stay perfectly in sync without drift.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Box, Building2, ChevronDown, ChevronRight, Clock,
  History, Home, MoreHorizontal, Plus, RefreshCw, Shield,
  Sparkles as SparklesIcon, StickyNote, Trash2, Truck, Users, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp } from "../lib/format";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

const cls = (...a) => a.filter(Boolean).join(" ");

const SECTIONS = [
  { key: "staff",       label: "Staff",                                  icon: Users },
  { key: "welfare",     label: "Welfare",                                icon: Home },
  { key: "plant",       label: "Plant, Materials Handling & Attendances", icon: Truck },
  { key: "scaffolding", label: "Scaffolding — Internal & External",       icon: Building2 },
  { key: "fencing",     label: "Temporary Fencing, Hoarding & Security",  icon: Shield },
  { key: "cleaning",    label: "Cleaning",                                icon: SparklesIcon },
  { key: "misc",        label: "Misc Items",                              icon: Box },
];

const CLIENT_CATEGORY_LABEL = {
  scaffolding: "Scaffolding",
  welfare:     "Welfare Facilities",
  management:  "Project Management & Supervision",
  sundries:    "Sundries (PPE, H&S, Protection, Skips & Waste, Cleaning, etc.)",
};
const CLIENT_CATEGORY_ORDER = ["scaffolding", "welfare", "management", "sundries"];

const UNIT_TYPE_LABEL = {
  weekly:               "weekly",
  item:                 "item",
  percentage_weekly:    "% × weekly",
  nr_weeks:             "qty × weeks",
  qty_only:             "qty × rate",
  percentage_section:   "% of section",
};

// Determines which cells are editable for each unit_type. Mirrors
// `derive_default_unit_type_to_columns()` on the backend so the UI doesn't
// need a server round-trip just to disable a cell.
function unitColumns(ut) {
  const weeks = ut === "weekly" || ut === "nr_weeks" || ut === "percentage_weekly";
  const qty   = ut !== "percentage_weekly" || true;  // qty always shown
  const rate  = ut !== "percentage_section";
  return { qty, weeks, rate };
}

const TRAFFIC_LIGHT_TONE = {
  green: { bg: "#E8EFE9", text: "#2F4F3E", border: "#D6E2D8", label: "On target" },
  amber: { bg: "#FEF3E2", text: "#7A4C0A", border: "#F2D9A6", label: "Outside target" },
  red:   { bg: "#FCE9E6", text: "#A1311E", border: "#F2C5BE", label: "Way off target" },
};

/* ============================================================
 * MasterStrip — 4 cards across the top
 * ============================================================ */
function MasterStrip({ summary, onAdjustWeeks, status }) {
  const [draft, setDraft] = useState(summary?.manual_duration_adjustment_weeks || 0);
  useEffect(() => {
    setDraft(summary?.manual_duration_adjustment_weeks || 0);
  }, [summary?.manual_duration_adjustment_weeks]);

  const tone = TRAFFIC_LIGHT_TONE[summary?.traffic_light] || TRAFFIC_LIGHT_TONE.amber;
  const adjBadge = (val) => {
    const v = Number(val) || 0;
    if (v === 0) return null;
    return (
      <span
        className="font-mono"
        style={{
          fontSize: 10,
          background: "#E8EFE9",
          color: "#2F4F3E",
          border: "1px solid #D6E2D8",
          padding: "1px 6px",
          borderRadius: 4,
          marginLeft: 8,
          letterSpacing: "0.04em",
          fontWeight: 600,
        }}
      >
        {v > 0 ? `+${v}` : v} wk
      </span>
    );
  };

  return (
    <div className="grid grid-cols-4 gap-3 mb-5">
      {/* Card 1 — Total Man Hours */}
      <div className="bg-surface border border-line rounded-lg p-4 shadow-card" data-testid="prelims-card-hours">
        <div className="col-head text-ink-3 mb-2">Total Man Hours</div>
        <div className="font-mono tnum text-ink" style={{ fontSize: 22, fontWeight: 600 }}>
          {Number(summary?.total_man_hours || 0).toLocaleString("en-GB")} hrs
        </div>
        <div className="text-ink-3 mt-1.5" style={{ fontSize: 11 }}>From Estimate</div>
      </div>

      {/* Card 2 — Total Man Days */}
      <div className="bg-surface border border-line rounded-lg p-4 shadow-card" data-testid="prelims-card-mandays">
        <div className="col-head text-ink-3 mb-2">Total Man Days</div>
        <div className="font-mono tnum text-ink" style={{ fontSize: 22, fontWeight: 600 }}>
          {(Number(summary?.total_man_hours || 0) / 8).toFixed(1).toLocaleString()} days
        </div>
        <div className="text-ink-3 mt-1.5" style={{ fontSize: 11 }}>hours ÷ 8</div>
      </div>

      {/* Card 3 — Project Duration */}
      <div
        className="bg-surface border border-line rounded-lg p-4 shadow-card"
        data-testid="prelims-card-duration"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="col-head text-ink-3 inline-flex items-center gap-1.5">
            <Clock size={11} /> Project Duration
          </div>
          {adjBadge(summary?.manual_duration_adjustment_weeks)}
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-mono tnum text-ink" style={{ fontSize: 22, fontWeight: 600 }}>
            {Number(summary?.project_duration_weeks || 0).toFixed(1)} wk
          </div>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-ink-3" style={{ fontSize: 11 }}>Manual adj.</span>
          <input
            type="number"
            step="0.5"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              const v = Number(draft) || 0;
              if (v !== Number(summary?.manual_duration_adjustment_weeks || 0)) {
                onAdjustWeeks(v);
              }
            }}
            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
            data-testid="prelims-duration-adjustment-input"
            className="w-16 h-7 px-2 rounded-md border border-line bg-surface text-ink font-mono tnum text-right focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 12 }}
            title="Will also be editable on Adjudication when that screen is built."
          />
          <span className="text-ink-3" style={{ fontSize: 11 }}>wk</span>
        </div>
        <div className="text-ink-3 mt-1.5 italic" style={{ fontSize: 10.5 }}>
          Calc {Number(summary?.calculated_duration_weeks || 0).toFixed(1)} wk · Adjudication-editable later
        </div>
      </div>

      {/* Card 4 — Net total + prelims % */}
      <div
        className="bg-surface border rounded-lg p-4 shadow-card"
        style={{ borderColor: tone.border }}
        data-testid="prelims-card-net-total"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="col-head text-ink-3">Net Total · Prelims %</div>
          <span
            data-testid="prelims-traffic-light"
            className="font-mono uppercase"
            style={{
              background: tone.bg, color: tone.text, border: `1px solid ${tone.border}`,
              padding: "1px 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", borderRadius: 4,
            }}
          >
            {tone.label}
          </span>
        </div>
        <div className="font-mono tnum text-ink" style={{ fontSize: 22, fontWeight: 600 }}>
          {gbp(summary?.net_total || 0)}
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span
            className="font-mono tnum"
            data-testid="prelims-pct-of-net"
            style={{ fontSize: 14, fontWeight: 600, color: tone.text }}
          >
            {Number(summary?.prelims_pct_of_net || 0).toFixed(1)}%
          </span>
          <span className="text-ink-3" style={{ fontSize: 11 }}>
            prelims · target 8–15% · status:&nbsp;{status}
          </span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
 * NoteButton + NoteContent — Fix 2/3/4: Shadcn Popover with author attribution
 * ============================================================
 * Visual states (Fix 2):
 *   • Empty   → outlined StickyNote in `text-ink-3` (text-tertiary)
 *   • Active  → filled   StickyNote in `text-info`
 *   • Hover   → tooltip via native `title`: "Add note" / "View note"
 *
 * Attribution (Fix 3): when a note exists, the popover header shows
 * `Note by {author} · edited DD/MM/YYYY HH:mm`. Author + timestamp are
 * stamped server-side on every PUT where the `notes` field changed.
 *
 * Collision avoidance (Fix 4): Shadcn Popover wraps Radix's Popover, which
 * supports `avoidCollisions` and `collisionPadding` natively — no manual
 * viewport math needed. The popover flips to `top` when below would clip.
 */
function formatNoteTimestamp(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const min = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  } catch {
    return "";
  }
}

function NoteButton({ item, onSave }) {
  const [open, setOpen] = useState(false);
  const hasNote = !!(item.notes && item.notes.trim());
  const [draft, setDraft] = useState(item.notes || "");

  // Reset draft each time the popover opens (so cancellations don't leak).
  useEffect(() => {
    if (open) setDraft(item.notes || "");
  }, [open, item.notes]);

  const persist = () => {
    const next = (draft || "").trim();
    if (next !== (item.notes || "").trim()) {
      onSave(next);
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={`prelim-note-toggle-${item.id}`}
          aria-label={hasNote ? "View note" : "Add note"}
          title={hasNote ? "View note" : "Add note"}
          className={cls(
            "p-1 rounded transition-colors focus:outline-none focus-visible:ring-2",
            hasNote
              ? "text-info hover:text-info-deep focus-visible:ring-info/40"
              : "text-ink-3 hover:text-ink-2 focus-visible:ring-accent/30"
          )}
        >
          <StickyNote
            size={12}
            // Filled appearance for the active state — Lucide SVG paths
            // accept `fill` directly, which `currentColor` resolves to the
            // info tone on the parent button.
            fill={hasNote ? "currentColor" : "none"}
            // Slightly heavier stroke for the active state so it reads as
            // "filled badge" against the row background.
            strokeWidth={hasNote ? 2 : 1.75}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={6}
        // Fix 4 — collision avoidance handed off to Radix.
        avoidCollisions
        collisionPadding={16}
        className="w-[300px] p-3 z-50"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        {hasNote && item.note_author_name && (
          <div
            className="text-ink-3 mb-2"
            data-testid={`prelim-note-attribution-${item.id}`}
            style={{ fontSize: 11 }}
          >
            <span className="text-ink-2" style={{ fontWeight: 500 }}>
              Note by {item.note_author_name}
            </span>
            {item.note_updated_at && (
              <span className="font-mono ml-1.5">
                · edited {formatNoteTimestamp(item.note_updated_at)}
              </span>
            )}
          </div>
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
          placeholder={hasNote ? "Edit note…" : "Add a note for this line…"}
          data-testid={`prelim-note-textarea-${item.id}`}
          className="w-full h-24 p-2 rounded border border-line bg-surface text-ink resize-none focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
          style={{ fontSize: 12 }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { persist(); }
            if (e.key === "Escape") { setOpen(false); }
          }}
        />
        <div className="flex items-center justify-between mt-2">
          {hasNote ? (
            <button
              onClick={() => { setDraft(""); }}
              data-testid={`prelim-note-clear-${item.id}`}
              className="text-note-red hover:underline"
              style={{ fontSize: 11 }}
            >
              Clear note
            </button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setOpen(false)}
              className="h-7 px-3 rounded text-ink-3 hover:text-ink-2"
              style={{ fontSize: 12 }}
            >
              Cancel
            </button>
            <button
              onClick={persist}
              data-testid={`prelim-note-save-${item.id}`}
              className="h-7 px-3 rounded bg-accent text-white hover:bg-accent-deep"
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              Save note
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ============================================================
 * RowMenu — three-dot menu (audit / reset / delete)
 * ============================================================ */
function RowMenu({ item, onOpenAudit, onReset, onDelete, anchorRef }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" ref={anchorRef}>
      <button
        onClick={() => setOpen((x) => !x)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid={`prelim-row-menu-${item.id}`}
        className="text-ink-3 hover:text-ink-2 p-1"
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-44 bg-surface border border-line rounded-md shadow-card overflow-hidden">
          <button
            onMouseDown={() => { onOpenAudit(item); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
            style={{ fontSize: 12 }}
          >
            <History size={12} className="text-ink-3" /> View audit history
          </button>
          {!item.is_custom && (
            <button
              onMouseDown={() => { onReset(item); setOpen(false); }}
              data-testid={`prelim-reset-${item.id}`}
              className="w-full text-left px-3 py-2 text-ink-2 hover:bg-surface-2 inline-flex items-center gap-2"
              style={{ fontSize: 12 }}
            >
              <RefreshCw size={12} className="text-ink-3" /> Reset to library
            </button>
          )}
          {item.is_custom && (
            <button
              onMouseDown={() => { onDelete(item); setOpen(false); }}
              data-testid={`prelim-delete-${item.id}`}
              className="w-full text-left px-3 py-2 text-note-red hover:bg-surface-2 inline-flex items-center gap-2"
              style={{ fontSize: 12 }}
            >
              <Trash2 size={12} /> Delete custom line
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
 * NumCell — auto-save number cell with override-aware visual
 * ============================================================ */
function NumCell({ value, onChange, disabled, prefix, suffix, overridden, dataTestId, decimals = 2 }) {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(formatNum(value, decimals)); }, [value, editing, decimals]);
  const onBlur = () => {
    setEditing(false);
    const parsed = parseFloat(draft);
    const next = isNaN(parsed) ? 0 : parsed;
    if (next !== Number(value)) onChange(next);
  };
  return (
    <div className={cls(
      "inline-flex items-center gap-1 px-2 h-7 rounded border bg-surface",
      disabled
        ? "border-line-soft text-ink-4 bg-surface-2/40"
        : "border-line text-ink hover:border-accent focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20",
      overridden && "border-warning/60",
    )}>
      {prefix && <span className="text-ink-3" style={{ fontSize: 11 }}>{prefix}</span>}
      <input
        type="text"
        inputMode="decimal"
        value={editing ? draft : formatNum(value, decimals)}
        onFocus={() => { setEditing(true); setDraft(formatNum(value, decimals)); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={onBlur}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
        disabled={disabled}
        data-testid={dataTestId}
        className="w-16 bg-transparent font-mono tnum text-right focus:outline-none disabled:cursor-not-allowed"
        style={{ fontSize: 12 }}
      />
      {suffix && <span className="text-ink-3" style={{ fontSize: 11 }}>{suffix}</span>}
      {overridden && (
        <span title="Manually overridden — differs from library default" className="text-warning" style={{ fontSize: 8 }}>◆</span>
      )}
    </div>
  );
}

function formatNum(v, decimals = 2) {
  const n = Number(v);
  if (!isFinite(n) || n === 0) return "0";
  return n.toFixed(decimals).replace(/\.?0+$/, "");
}

/* ============================================================
 * LineRow
 * ============================================================ */
function LineRow({ item, onChange, onOpenAudit, onReset, onDelete }) {
  const cols = unitColumns(item.unit_type);
  const isPercentSection = item.unit_type === "percentage_section";
  const hasNote = !!(item.notes && item.notes.trim());

  const set = (patch) => onChange(patch);

  return (
    <tr
      data-testid={`prelim-row-${item.id}`}
      className={cls("border-b border-line-soft hover:bg-surface-2/40 group",
        isPercentSection && "bg-accent-soft/30")}
    >
      <td className="px-3 py-2 align-middle">
        <div className="flex items-center gap-1.5">
          {hasNote && (
            <span
              title={item.notes}
              data-testid={`prelim-note-indicator-${item.id}`}
              className="text-info flex-shrink-0"
              style={{ fontSize: 9 }}
            >
              ●
            </span>
          )}
          {item.is_custom && (
            <span
              className="font-mono uppercase"
              style={{ background: "#E8EFE9", color: "#2F4F3E", padding: "1px 5px", fontSize: 9, fontWeight: 600, borderRadius: 3 }}
            >
              Custom
            </span>
          )}
          <span className="text-ink" style={{ fontSize: 12.5 }}>{item.item_name}</span>
        </div>
      </td>
      <td className="px-3 py-2 align-middle text-right">
        <NumCell
          value={item.quantity}
          onChange={(v) => set({ quantity: v })}
          disabled={!cols.qty}
          suffix={item.unit_type === "percentage_weekly" ? "%" : ""}
          overridden={item.quantity_overridden}
          dataTestId={`prelim-qty-${item.id}`}
        />
      </td>
      <td className="px-3 py-2 align-middle text-right">
        <NumCell
          value={item.weeks}
          onChange={(v) => set({ weeks: v })}
          disabled={!cols.weeks}
          suffix="wk"
          overridden={item.weeks_overridden}
          dataTestId={`prelim-weeks-${item.id}`}
          decimals={1}
        />
      </td>
      <td className="px-3 py-2 align-middle text-right">
        <NumCell
          value={item.rate}
          onChange={(v) => set({ rate: v })}
          disabled={!cols.rate}
          prefix="£"
          overridden={item.rate_overridden}
          dataTestId={`prelim-rate-${item.id}`}
        />
      </td>
      <td className="px-3 py-2 align-middle text-right">
        <span
          className="font-mono tnum text-ink"
          style={{ fontSize: 13, fontWeight: 500 }}
          data-testid={`prelim-total-${item.id}`}
        >
          {gbp(item.total || 0)}
        </span>
      </td>
      <td className="px-2 py-2 align-middle">
        <span
          className="font-mono uppercase text-ink-3"
          style={{ fontSize: 9, letterSpacing: "0.05em" }}
          title={`unit_type = ${item.unit_type}`}
        >
          {UNIT_TYPE_LABEL[item.unit_type] || item.unit_type}
        </span>
      </td>
      <td className="px-2 py-2 align-middle">
        <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
          <NoteButton item={item} onSave={(v) => set({ notes: v })} />
          <RowMenu
            item={item}
            onOpenAudit={onOpenAudit}
            onReset={onReset}
            onDelete={onDelete}
          />
        </div>
      </td>
    </tr>
  );
}

/* ============================================================
 * AddCustomRow — inline form to add a custom line
 * ============================================================ */
function AddCustomRow({ section, onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  // LT-Prelims-02 (Fix 1) — `weekly` is the most common pattern for prelims
  // (e.g. crew + plant), so default new custom lines to that. The dropdown
  // intentionally omits `percentage_section` (reserved for the seeded
  // scaffolding 10% line — not valid for ad-hoc lines).
  const [unitType, setUnitType] = useState("weekly");
  if (!open) {
    return (
      <tr>
        <td colSpan={7} className="px-3 py-2">
          <button
            onClick={() => setOpen(true)}
            data-testid={`prelim-add-custom-${section}`}
            className="inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-accent-soft text-accent hover:bg-accent hover:text-white transition-colors"
            style={{ fontSize: 11.5, fontWeight: 500 }}
          >
            <Plus size={12} /> Add custom line
          </button>
        </td>
      </tr>
    );
  }
  const submit = () => {
    if (!name.trim()) {
      toast.error("Item name is required");
      return;
    }
    onAdd({ item_name: name.trim(), unit_type: unitType });
    setName(""); setUnitType("weekly"); setOpen(false);
  };
  return (
    <tr style={{ background: "#F5F3EE" }}>
      <td colSpan={7} className="px-3 py-2">
        <div className="flex items-center gap-2">
          <input
            value={name} onChange={(e) => setName(e.target.value)}
            autoFocus
            placeholder="Custom item name…"
            data-testid={`prelim-custom-name-${section}`}
            className="flex-1 h-7 px-2 rounded border border-line bg-surface text-ink focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
            style={{ fontSize: 12 }}
            onKeyDown={(e) => { if (e.key === "Enter") submit(); if (e.key === "Escape") setOpen(false); }}
          />
          <select
            value={unitType} onChange={(e) => setUnitType(e.target.value)}
            data-testid={`prelim-custom-unit-type-${section}`}
            className="h-7 px-2 rounded border border-line bg-surface text-ink-2"
            style={{ fontSize: 11.5 }}
          >
            <option value="weekly">weekly (qty × weeks × rate)</option>
            <option value="item">item (qty × rate)</option>
            <option value="qty_only">qty × rate (no weeks)</option>
            <option value="nr_weeks">qty × weeks × rate</option>
            <option value="percentage_weekly">% × weeks × rate</option>
          </select>
          <button onClick={submit}
            className="h-7 px-3 rounded bg-accent text-white hover:bg-accent-deep"
            style={{ fontSize: 11.5, fontWeight: 500 }}
            data-testid={`prelim-custom-submit-${section}`}
          >Add</button>
          <button onClick={() => setOpen(false)} className="h-7 px-2 text-ink-3 hover:text-ink-2"
            style={{ fontSize: 11.5 }}
          >Cancel</button>
        </div>
      </td>
    </tr>
  );
}

/* ============================================================
 * SectionCard
 * ============================================================ */
function SectionCard({
  section, items, sectionSubtotal, hideUnused, collapsed, onToggleCollapse,
  onUpdateItem, onResetItem, onDeleteItem, onOpenAudit, onAddCustom,
}) {
  const filtered = hideUnused
    ? items.filter((it) => Number(it.quantity) > 0 || Number(it.total) > 0 || it.unit_type === "percentage_section")
    : items;
  const Icon = section.icon;
  const visibleCount = filtered.length;
  const totalCount = items.length;

  return (
    <div
      className="bg-surface border border-line rounded-lg overflow-hidden mb-4"
      style={{
        boxShadow:
          "0 1px 0 rgba(0,0,0,0.02), 0 6px 12px -6px rgba(58, 50, 32, 0.08), 0 2px 4px -2px rgba(58, 50, 32, 0.04)",
      }}
      data-testid={`prelim-section-${section.key}`}
    >
      <button
        onClick={() => onToggleCollapse(section.key)}
        data-testid={`prelim-section-toggle-${section.key}`}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-surface-2/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          {collapsed
            ? <ChevronRight size={14} className="text-ink-3" />
            : <ChevronDown size={14} className="text-ink-3" />}
          <Icon size={14} className="text-accent" />
          <span className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            {section.label}
          </span>
          <span className="text-ink-3" style={{ fontSize: 11.5 }}>
            ({visibleCount}{hideUnused && totalCount !== visibleCount ? ` / ${totalCount}` : ""})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
            {gbp(sectionSubtotal || 0)}
          </span>
        </div>
      </button>
      {!collapsed && (
        <table className="w-full">
          <thead>
            <tr className="border-y border-line bg-surface-2/40">
              <th className="col-head px-3 py-2 text-left">Item</th>
              <th className="col-head px-3 py-2 text-right" style={{ width: 100 }}>Qty</th>
              <th className="col-head px-3 py-2 text-right" style={{ width: 100 }}>Weeks</th>
              <th className="col-head px-3 py-2 text-right" style={{ width: 110 }}>Rate</th>
              <th className="col-head px-3 py-2 text-right" style={{ width: 110 }}>Total</th>
              <th className="col-head px-2 py-2" style={{ width: 100 }}>Type</th>
              <th style={{ width: 70 }} />
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-ink-3 italic" style={{ fontSize: 12 }}>
                  No items{hideUnused ? " with quantity > 0" : ""} in this section.
                </td>
              </tr>
            )}
            {filtered.map((it) => (
              <LineRow
                key={it.id}
                item={it}
                onChange={(patch) => onUpdateItem(it.id, patch)}
                onOpenAudit={() => onOpenAudit(it)}
                onReset={() => onResetItem(it)}
                onDelete={() => onDeleteItem(it)}
              />
            ))}
            <AddCustomRow section={section.key} onAdd={(payload) => onAddCustom(section.key, payload)} />
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ============================================================
 * ClientBreakdownPanel
 * ============================================================ */
function ClientBreakdownPanel({ summary, items }) {
  const [expandedCat, setExpandedCat] = useState(null);
  const cat_totals = summary?.client_category_totals || {};
  const grand = summary?.grand_total || 0;

  const itemsByCat = useMemo(() => {
    const m = {};
    for (const it of items || []) {
      const c = it.client_category || "sundries";
      (m[c] ||= []).push(it);
    }
    return m;
  }, [items]);

  return (
    <div
      className="bg-surface border border-line rounded-lg overflow-hidden mt-2"
      style={{
        boxShadow:
          "0 1px 0 rgba(0,0,0,0.02), 0 6px 14px -6px rgba(58, 50, 32, 0.10), 0 3px 5px -2px rgba(58, 50, 32, 0.06)",
      }}
      data-testid="prelims-client-breakdown"
    >
      <div className="px-5 py-3 border-b border-line bg-accent-soft/30">
        <h3 className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
          Client-facing breakdown
        </h3>
        <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>
          How prelims appear on the client estimate. Click a category to see contributing line items.
        </div>
      </div>
      {CLIENT_CATEGORY_ORDER.map((cat) => {
        const total = cat_totals[cat] || 0;
        const list = itemsByCat[cat] || [];
        const populated = list.filter((it) => Number(it.total) > 0);
        const isOpen = expandedCat === cat;
        return (
          <div key={cat} className="border-b border-line-soft">
            <button
              onClick={() => setExpandedCat(isOpen ? null : cat)}
              data-testid={`prelims-cat-toggle-${cat}`}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-surface-2/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                {isOpen
                  ? <ChevronDown size={12} className="text-ink-3" />
                  : <ChevronRight size={12} className="text-ink-3" />}
                <span className="text-ink" style={{ fontSize: 13, fontWeight: 500 }}>
                  {CLIENT_CATEGORY_LABEL[cat]}
                </span>
                <span className="text-ink-3" style={{ fontSize: 11 }}>
                  ({populated.length} item{populated.length === 1 ? "" : "s"})
                </span>
              </div>
              <span className="font-mono tnum text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                {gbp(total)}
              </span>
            </button>
            {isOpen && (
              <div className="px-8 pb-3">
                {populated.length === 0 ? (
                  <div className="text-ink-3 italic py-2" style={{ fontSize: 11.5 }}>No items contributing yet.</div>
                ) : (
                  <table className="w-full">
                    <tbody>
                      {populated.map((it) => (
                        <tr key={it.id} className="border-b border-line-soft last:border-b-0">
                          <td className="py-1.5 text-ink-2" style={{ fontSize: 11.5 }}>{it.item_name}</td>
                          <td className="py-1.5 text-right font-mono tnum text-ink" style={{ fontSize: 11.5 }}>
                            {gbp(it.total || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
      <div className="px-5 py-3 bg-accent-soft/40 flex items-center justify-between">
        <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
          Total Prelims
        </span>
        <span
          className="font-mono tnum text-ink"
          style={{ fontSize: 16, fontWeight: 700 }}
          data-testid="prelims-grand-total"
        >
          {gbp(grand)}
        </span>
      </div>
    </div>
  );
}

/* ============================================================
 * AuditDrawer (right-side, 560px)
 * ============================================================ */
function AuditDrawer({ open, item, history, onClose }) {
  if (!open) return null;
  return (
    <>
      <div className="fixed inset-0 bg-ink/30 z-40" onClick={onClose} />
      <div
        className="fixed right-0 top-0 bottom-0 z-50 bg-surface border-l border-line shadow-card overflow-hidden flex flex-col"
        style={{ width: 560 }}
        data-testid="prelim-audit-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-line">
          <div>
            <div className="font-display text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
              Audit history
            </div>
            <div className="text-ink-3 mt-0.5" style={{ fontSize: 11.5 }}>{item?.item_name}</div>
          </div>
          <button onClick={onClose} className="text-ink-3 hover:text-ink" data-testid="prelim-audit-close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto">
          {(!history || history.length === 0) ? (
            <div className="text-center py-12 text-ink-3" style={{ fontSize: 12.5 }}>
              No history yet for this line.
            </div>
          ) : (
            <div className="divide-y divide-line-soft">
              {history.map((e, idx) => {
                const ts = e.created_at ? new Date(e.created_at).toLocaleString("en-GB") : "";
                const meta = e.metadata || {};
                let label = e.event_type;
                let detail = "";
                if (e.event_type === "prelim_item_field_changed") {
                  label = `Changed ${meta.field}`;
                  detail = `${formatAuditValue(meta.previous)} → ${formatAuditValue(meta.new)}`;
                } else if (e.event_type === "prelim_item_reset") {
                  label = "Reset to library default";
                } else if (e.event_type === "prelim_item_added_custom") {
                  label = "Added as custom line";
                } else if (e.event_type === "prelim_item_deleted_custom") {
                  label = "Deleted custom line";
                }
                return (
                  <div key={idx} className="px-5 py-3" data-testid={`audit-row-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-ink" style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</span>
                      <span className="text-ink-3 font-mono" style={{ fontSize: 11 }}>{ts}</span>
                    </div>
                    {detail && (
                      <div className="text-ink-3 mt-1 font-mono tnum" style={{ fontSize: 11.5 }}>
                        {detail}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function formatAuditValue(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString("en-GB");
  return String(v);
}

/* ============================================================
 * Main TenderPrelims component
 * ============================================================ */
export default function TenderPrelims({ tender, onTenderUpdate }) {
  const [data, setData] = useState({ items: [], summary: null, tender: null });
  const [loading, setLoading] = useState(true);
  const [hideUnused, setHideUnused] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({});
  const [auditDrawer, setAuditDrawer] = useState({ item: null, history: [] });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [status, setStatus] = useState("loaded");

  // Initial load
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tender.id]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/tenders/${tender.id}/prelims`);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load prelims");
    } finally {
      setLoading(false);
    }
  };

  // ----- Save flow: optimistic update + 800ms debounce -----
  const saveTimer = useRef(null);
  const pendingChanges = useRef({}); // { itemId: patch }
  const flushSave = async () => {
    const all = pendingChanges.current;
    pendingChanges.current = {};
    if (Object.keys(all).length === 0) return;
    setStatus("saving");
    try {
      let lastResp = null;
      for (const [itemId, patch] of Object.entries(all)) {
        const r = await api.put(`/tenders/${tender.id}/prelims/${itemId}`, patch);
        lastResp = r.data;
      }
      if (lastResp) {
        setData(lastResp);
        if (lastResp.tender) onTenderUpdate?.(lastResp.tender);
      }
      setStatus("saved");
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Save failed — your changes are safe locally. Will retry on next edit.");
    }
  };

  const queueSave = (itemId, patch) => {
    pendingChanges.current[itemId] = { ...(pendingChanges.current[itemId] || {}), ...patch };
    setStatus("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flushSave, 800);
  };

  // Optimistic local merge then queue PUT.
  const onUpdateItem = (itemId, patch) => {
    setData((d) => ({
      ...d,
      items: d.items.map((it) => it.id === itemId ? { ...it, ...patch } : it),
    }));
    queueSave(itemId, patch);
  };

  const onResetItem = async (item) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/prelims/${item.id}/reset`);
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success(`Reset “${item.item_name}” to library default`);
    } catch (e) {
      console.error(e);
      toast.error("Reset failed");
    }
  };

  const onDeleteItem = async (item) => {
    if (!window.confirm(`Delete “${item.item_name}”?`)) return;
    try {
      const r = await api.delete(`/tenders/${tender.id}/prelims/${item.id}`);
      setData((d) => ({
        ...d,
        items: r.data.items,
        summary: r.data.summary,
        tender: r.data.tender,
      }));
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success("Custom line deleted");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onAddCustom = async (section, payload) => {
    try {
      const r = await api.post(`/tenders/${tender.id}/prelims`, {
        item_name: payload.item_name,
        section,
        unit_type: payload.unit_type,
        quantity: 0,
        weeks: 0,
        rate: 0,
      });
      setData((d) => ({
        ...d,
        items: r.data.items,
        summary: r.data.summary,
        tender: r.data.tender,
      }));
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      toast.success(`Added “${payload.item_name}”`);
    } catch (e) {
      console.error(e);
      toast.error("Failed to add custom line");
    }
  };

  const onAdjustWeeks = async (val) => {
    setStatus("saving");
    try {
      const r = await api.patch(`/tenders/${tender.id}/duration`, {
        manual_duration_adjustment_weeks: val,
      });
      setData(r.data);
      if (r.data.tender) onTenderUpdate?.(r.data.tender);
      setStatus("saved");
    } catch (e) {
      console.error(e);
      setStatus("error");
      toast.error("Failed to update duration");
    }
  };

  const onOpenAudit = async (item) => {
    setAuditDrawer({ item, history: [] });
    setDrawerOpen(true);
    try {
      const r = await api.get(`/tenders/${tender.id}/prelims/audit/${item.id}`);
      setAuditDrawer({ item, history: r.data });
    } catch (e) {
      console.error(e);
    }
  };

  // Group items by section.
  const itemsBySection = useMemo(() => {
    const m = {};
    for (const sect of SECTIONS) m[sect.key] = [];
    for (const it of data.items || []) {
      (m[it.section] ||= []).push(it);
    }
    for (const k in m) m[k].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
    return m;
  }, [data.items]);

  if (loading) {
    return (
      <div className="mt-6 text-ink-3 py-12 text-center" style={{ fontSize: 12 }}>
        Loading prelims…
      </div>
    );
  }

  const allCollapsed = SECTIONS.every((s) => collapsedSections[s.key]);
  const toggleAllSections = () => {
    if (allCollapsed) {
      setCollapsedSections({});
    } else {
      const all = {};
      SECTIONS.forEach((s) => { all[s.key] = true; });
      setCollapsedSections(all);
    }
  };

  return (
    <div className="mt-6">
      <MasterStrip
        summary={data.summary}
        onAdjustWeeks={onAdjustWeeks}
        status={status === "saving" ? "saving…" : status === "error" ? "error" : "saved"}
      />

      {/* Toolbar: Hide unused / Collapse-all + auto-save status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-ink-2 cursor-pointer" style={{ fontSize: 12 }}>
            <input
              type="checkbox"
              checked={hideUnused}
              onChange={(e) => setHideUnused(e.target.checked)}
              data-testid="prelims-hide-unused-toggle"
            />
            Hide unused items (qty = 0)
          </label>
          <button
            onClick={toggleAllSections}
            data-testid="prelims-collapse-all"
            className="h-7 px-3 rounded-md border border-line bg-surface text-ink-2 hover:bg-surface-2 transition-colors"
            style={{ fontSize: 11.5 }}
          >
            {allCollapsed ? "Expand all" : "Collapse all"}
          </button>
        </div>
        <div className="text-ink-3 inline-flex items-center gap-2" style={{ fontSize: 11.5 }}>
          {status === "saving" && (
            <>
              <span className="inline-block w-1.5 h-1.5 bg-warning rounded-full animate-pulse" />
              Saving…
            </>
          )}
          {status === "saved" && (
            <>
              <span className="inline-block w-1.5 h-1.5 bg-accent rounded-full" />
              Saved
            </>
          )}
          {status === "error" && (
            <>
              <AlertTriangle size={12} className="text-note-red" />
              Save error
            </>
          )}
        </div>
      </div>

      {SECTIONS.map((section) => (
        <SectionCard
          key={section.key}
          section={section}
          items={itemsBySection[section.key] || []}
          sectionSubtotal={data.summary?.section_subtotals?.[section.key] || 0}
          hideUnused={hideUnused}
          collapsed={!!collapsedSections[section.key]}
          onToggleCollapse={(key) =>
            setCollapsedSections((p) => ({ ...p, [key]: !p[key] }))
          }
          onUpdateItem={onUpdateItem}
          onResetItem={onResetItem}
          onDeleteItem={onDeleteItem}
          onOpenAudit={onOpenAudit}
          onAddCustom={onAddCustom}
        />
      ))}

      <ClientBreakdownPanel summary={data.summary} items={data.items} />

      <AuditDrawer
        open={drawerOpen}
        item={auditDrawer.item}
        history={auditDrawer.history}
        onClose={() => setDrawerOpen(false)}
      />
    </div>
  );
}
